import { useNavigate } from "@entry/navigation";
import { useState } from "react";

import { AmountAdjuster } from "@components/AmountAdjuster";
import { SidecarBadge, trumpContributionFrequencyOptions } from "@components/Goals/TrumpSidecar";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { SegmentedControl } from "@components/SegmentedControl";
import { Spacing, SpacingSizes } from "@components/Spacing";
import {
  DEFAULT_DEPOSIT_ADJUSTMENT,
  DEFAULT_RECURRING_AMOUNT,
  MAX_DEPOSIT_AMOUNT,
} from "@constants";
import { Flow, Routes, TrumpContributionFrequency } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { useGoalFlow, useTrumpSidecar } from "@hooks";
import { Translations, i18n } from "@i18n";
import { convertCentsToDollar, convertDollarToCents } from "@utils";

const MIN_CONTRIBUTION_AMOUNT = 0;

export const TrumpSidecarCurrentContributionRoute = () => {
  const navigate = useNavigate();

  const {
    goalProps: { flow, trumpContributionAmountInCents, trumpContributionFrequency },
    setGoalProps,
  } = useGoalFlow();

  const [amount, setAmount] = useState(
    trumpContributionAmountInCents === undefined
      ? DEFAULT_RECURRING_AMOUNT
      : convertCentsToDollar(trumpContributionAmountInCents),
  );
  const [frequency, setFrequency] = useState(
    trumpContributionFrequency ?? TrumpContributionFrequency.Weekly,
  );

  const { firstName, isSelf } = useTrumpSidecar();

  // Fall back to the first person copy when no name is available.
  const shouldUseSelfCopy = isSelf || !firstName;

  const handleSubmit = () => {
    setGoalProps({
      trumpContributionAmountInCents: convertDollarToCents(amount),
      trumpContributionFrequency: frequency,
    });

    navigate(Routes.TrumpSidecarTaxCalculation);
  };

  const props: OnboardingLayoutProps = {
    title: shouldUseSelfCopy
      ? i18n.t(Translations.TrumpSidecarCurrentContributionRouteTitleSelf)
      : i18n.t(Translations.TrumpSidecarCurrentContributionRouteTitle, {
          firstName,
        }),
    hideLogout: flow !== Flow.Onboarding,
    cta: {
      primary: {
        title: i18n.t(Translations.TrumpSidecarCurrentContributionRoutePrimaryCTA),
        onClick: handleSubmit,
      },
    },
  };

  return (
    <OnboardingLayout {...props}>
      <SidecarBadge>{i18n.t(Translations.TrumpSidecarCurrentContributionRouteBadge)}</SidecarBadge>
      <Spacing size={SpacingSizes.M} />

      <AmountAdjuster
        amount={amount}
        onAmountChange={setAmount}
        min={MIN_CONTRIBUTION_AMOUNT}
        max={MAX_DEPOSIT_AMOUNT}
        step={DEFAULT_DEPOSIT_ADJUSTMENT}
      />
      <Spacing size={SpacingSizes.M} />

      <SegmentedControl
        options={trumpContributionFrequencyOptions}
        selectedKey={frequency}
        onSelect={(key) => setFrequency(key as TrumpContributionFrequency)}
      />
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
